import moment from "moment-timezone";
import i18n from "i18next";
import {
  getRdModalityLabel,
  isInPersonModality,
  isTelehealthModality,
  mapSlotLocationToLabel,
  MODALITY_JOIN_SEPARATOR,
} from "../Dietitian/registrationMatchedRd";

export const DEFAULT_APPOINTMENT_SESSION_DURATION_MINUTES = 50;
export const APPOINTMENT_SESSION_DURATION_LABEL_KEY =
  "NUTRITION_TIMESLOT_LBL_SESSION_DURATION";

export const getRegistrationTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const resolveRdTimeslotId = (rdId, rawProfile) => {
  const fromRaw =
    rawProfile?.id ??
    rawProfile?.rd_id ??
    rawProfile?.provider_id ??
    rawProfile?.user_id;
  const resolved = fromRaw ?? rdId;
  const parsed = Number(resolved);
  return Number.isNaN(parsed) ? resolved : parsed;
};

export const MAX_TIMESLOT_WEEK_OFFSET = 8;
/** Alias matching iOS MAX_WEEK_INDEX — browse current week through 8 weeks ahead. */
export const MAX_TIMESLOT_WEEK_INDEX = MAX_TIMESLOT_WEEK_OFFSET;

/**
 * Date window for timeslot weekIndex (weeks run Monday–Sunday).
 * weekIndex 0 → today → coming Sunday (current, possibly partial week).
 * weekIndex k ≥ 1 → Monday–Sunday of the k-th following week.
 * Monday of week k = (Sunday of week 0) + 1 + 7 × (k − 1).
 */
export const buildTimeslotDateRange = (
  timezone = getRegistrationTimezone(),
  weekIndex = 0
) => {
  const tz = timezone || getRegistrationTimezone();
  const safeIndex = Math.max(
    0,
    Math.min(MAX_TIMESLOT_WEEK_INDEX, Number(weekIndex) || 0)
  );
  const today = moment.tz(tz).startOf("day");
  const week0Sunday = today.clone().endOf("isoWeek").startOf("day");

  let start;
  let end;

  if (safeIndex === 0) {
    start = today.clone();
    end = week0Sunday.clone();
  } else {
    start = week0Sunday
      .clone()
      .add(1 + 7 * (safeIndex - 1), "days")
      .startOf("day");
    end = start.clone().add(6, "days");
  }

  if (end.isBefore(start, "day")) {
    end = start.clone();
  }

  return {
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    timezone: tz,
  };
};

export const getTimeslotWeekOffsetFromStartDate = (
  startDate,
  timezone = getRegistrationTimezone()
) => {
  if (!startDate) {
    return 0;
  }
  const tz = timezone || getRegistrationTimezone();
  const today = moment.tz(tz).startOf("day");
  const start = moment.tz(startDate, "YYYY-MM-DD", tz).startOf("day");
  if (!start.isValid()) {
    return 0;
  }

  const week0Sunday = today.clone().endOf("isoWeek").startOf("day");

  // Week 0 window is [today, week0Sunday] (and any earlier cached start).
  if (start.isSameOrBefore(week0Sunday, "day")) {
    return 0;
  }

  // Week k ≥ 1 starts on Monday after week0Sunday.
  const daysAfterSunday = start.diff(week0Sunday, "days");
  const weekIndex = 1 + Math.floor((daysAfterSunday - 1) / 7);
  return Math.max(0, Math.min(MAX_TIMESLOT_WEEK_INDEX, weekIndex));
};

export const buildListTimeslotByRdPayload = (
  rdId,
  rawProfile,
  timezone,
  weekOffset = 0
) => {
  const dates = buildTimeslotDateRange(timezone, weekOffset);
  return {
    id: resolveRdTimeslotId(rdId, rawProfile),
    start_date: dates.start_date,
    end_date: dates.end_date,
    timezone: dates.timezone,
  };
};

const getPeriodId = (hour) => {
  if (hour < 12) {
    return "morning";
  }
  if (hour < 17) {
    return "afternoon";
  }
  return "evening";
};

const isFlatTimeslot = (item) =>
  Boolean(item?.start_time ?? item?.startTime) &&
  !Array.isArray(item?.slots);

export const isFlatRdTimeslotList = (slotList) =>
  Array.isArray(slotList) &&
  slotList.length > 0 &&
  slotList.every((item) => isFlatTimeslot(item));

const groupFlatTimeslotsByDate = (slotList, days) => {
  const daySlotMap = new Map(days.map((day) => [day.dateKey, []]));

  slotList.forEach((slot) => {
    const startTime = slot?.start_time ?? slot?.startTime;
    const dateKey =
      slot?.date ??
      slot?.slot_date ??
      (startTime ? moment(startTime).format("YYYY-MM-DD") : null);

    if (!dateKey) {
      return;
    }

    if (daySlotMap.has(dateKey)) {
      daySlotMap.get(dateKey).push(slot);
      return;
    }

    const matchedDay = days.find((day) => day.dateKey === dateKey);
    if (matchedDay) {
      daySlotMap.get(matchedDay.dateKey).push(slot);
    }
  });

  return daySlotMap;
};

const normalizeRdSlotDays = (rdSlot, days) => {
  const daySlotMap = new Map(days.map((day) => [day.dateKey, []]));
  const slotList = Array.isArray(rdSlot) ? rdSlot : [];

  if (slotList.length > 0 && isFlatTimeslot(slotList[0])) {
    return groupFlatTimeslotsByDate(slotList, days);
  }

  slotList.forEach((dayBlock, index) => {
    const dateKey =
      dayBlock?.date ??
      dayBlock?.slot_date ??
      dayBlock?.start_date ??
      days[index]?.dateKey;
    if (!dateKey) {
      return;
    }

    const slots = Array.isArray(dayBlock?.slots) ? dayBlock.slots : [];
    if (slots.length === 0) {
      return;
    }

    if (daySlotMap.has(dateKey)) {
      daySlotMap.set(dateKey, slots);
    } else if (days[index]) {
      daySlotMap.set(days[index].dateKey, slots);
    }
  });

  return daySlotMap;
};

export const extractSchedulingUrl = (slot) =>
  slot?.scheduling_url ?? slot?.schedulingUrl ?? "";

const pickSessionDescription = (...candidates) => {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return "";
};

const isNoPreferenceRegistration = (registrationData = {}) =>
  Boolean(
    registrationData?.selectedDietitian?.noPreference ||
      String(registrationData?.selectedDietitian?.id ?? "") === "no-preference"
  );

const extractWeekModalityLabelFromSlots = (daySlotMap, days) => {
  for (const day of days) {
    const slots = daySlotMap.get(day.dateKey) || [];
    for (const slot of slots) {
      const candidate = pickSessionDescription(
        slot?.visit_type_label,
        slot?.visit_type,
        slot?.modality,
        slot?.type,
        slot?.location
      );
      const mapped = mapSlotLocationToLabel(candidate);
      if (mapped) {
        return mapped;
      }
    }
  }
  return "";
};

const buildSlotSessionDescription = (
  value,
  durationMinutes = DEFAULT_APPOINTMENT_SESSION_DURATION_MINUTES
) => {
  const modality = mapSlotLocationToLabel(value);
  if (isInPersonModality(modality)) {
    return i18n.t("NUTRITION_TIMESLOT_DESC_IN_PERSON_SESSION");
  }
  if (isTelehealthModality(modality)) {
    return i18n.t("NUTRITION_TIMESLOT_DESC_TELEHEALTH_SESSION");
  }
  return "";
};

export const getSelectedSlotLocationLabel = (selectedSlot) => {
  if (!selectedSlot) {
    return "";
  }

  const location = pickSessionDescription(
    selectedSlot?.location,
    selectedSlot?.raw?.location
  );

  return mapSlotLocationToLabel(location);
};

export const getAppointmentModalityLabel = (
  registrationData = {},
  appointmentWeek = null
) => {
  if (isNoPreferenceRegistration(registrationData)) {
    return appointmentWeek?.modalityLabel || "";
  }

  const rd = registrationData?.selectedDietitian?.raw;
  return getRdModalityLabel(rd);
};

export const getAppointmentWeekSessionInfo = (modalityLabel, translate) => {
  if (!modalityLabel) {
    return translate("NUTRITION_TIMESLOT_LBL_SESSION_INFO");
  }

  if (modalityLabel.includes(MODALITY_JOIN_SEPARATOR)) {
    return modalityLabel
      .split(MODALITY_JOIN_SEPARATOR)
      .map((part) => translateAppointmentModalityLabel(part.trim(), translate))
      .filter(Boolean)
      .join(MODALITY_JOIN_SEPARATOR);
  }

  return translateAppointmentModalityLabel(modalityLabel, translate) || modalityLabel;
};

export const translateAppointmentModalityLabel = (label, translate) => {
  if (isInPersonModality(label)) {
    return translate("NUTRITION_COMMON_LBL_IN_PERSON");
  }
  if (isTelehealthModality(label)) {
    return translate("NUTRITION_COMMON_LBL_TELEHEALTH");
  }
  return label ?? "";
};

export const getSelectedDietitianDisplayName = (registrationData = {}) => {
  const selected = registrationData?.selectedDietitian;
  const isSpecificDietitian =
    selected &&
    !selected.noPreference &&
    String(selected.id ?? "") !== "no-preference";

  if (isSpecificDietitian) {
    const raw = selected.raw;
    if (raw && typeof raw === "object") {
      const firstName = raw.first_name ?? raw.firstName ?? "";
      const lastName = raw.last_name ?? raw.lastName ?? "";
      const name = `${firstName} ${lastName}`.trim();
      if (name) {
        return name;
      }
    }
    if (selected.name) {
      return selected.name;
    }
  }

  const noPrefRd = registrationData?.noPreferenceSelectedRd;
  const fromNoPrefView =
    noPrefRd?.view?.fullName ?? noPrefRd?.view?.name ?? "";
  if (fromNoPrefView) {
    return fromNoPrefView;
  }

  const fromNoPrefRaw = noPrefRd?.raw;
  if (fromNoPrefRaw && typeof fromNoPrefRaw === "object") {
    const firstName =
      fromNoPrefRaw.first_name ?? fromNoPrefRaw.firstName ?? "";
    const lastName = fromNoPrefRaw.last_name ?? fromNoPrefRaw.lastName ?? "";
    const name = `${firstName} ${lastName}`.trim();
    if (name) {
      return name;
    }
  }

  return "";
};

export const getSlotPreviewSubtext = (
  registrationData,
  appointmentWeek,
  selectedSlot,
  translate = (key) => i18n.t(key)
) => {
  if (!selectedSlot) {
    return "";
  }

  const locationLabel = getSelectedSlotLocationLabel(selectedSlot);
  const dietitianName = getSelectedDietitianDisplayName(registrationData);

  if (isNoPreferenceRegistration(registrationData)) {
    return locationLabel || "";
  }

  if (locationLabel && dietitianName) {
    return `${locationLabel}${MODALITY_JOIN_SEPARATOR}${dietitianName}`;
  }

  return locationLabel || dietitianName || "";
};

export const getSlotSessionDescription = (selectedSlot, appointmentWeek) => {
  const candidates = [
    selectedSlot?.raw?.visit_type_label,
    selectedSlot?.raw?.visit_type,
    selectedSlot?.raw?.modality,
    selectedSlot?.type,
    selectedSlot?.location,
    selectedSlot?.raw?.location,
    appointmentWeek?.modalityLabel,
  ];

  for (const candidate of candidates) {
    const description = buildSlotSessionDescription(candidate);
    if (description) {
      return description;
    }
  }

  return "";
};

export const mapRdSlotResponseToAppointmentWeek = (
  rdSlot,
  startDate,
  endDate,
  timezone = getRegistrationTimezone()
) => {
  const tz = timezone || getRegistrationTimezone();
  const start = moment.tz(startDate, tz).startOf("day");
  const end = moment.tz(endDate, tz).startOf("day");

  const days = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    const dateKey = cursor.format("YYYY-MM-DD");
    days.push({
      label: cursor.format("ddd"),
      date: cursor.format("MMM D"),
      dayKey: cursor.format("ddd MMM D"),
      dateKey,
    });
    cursor.add(1, "day");
  }

  const daySlotMap = normalizeRdSlotDays(rdSlot, days);
  const candidateDays = days.filter((day) => {
    const slots = daySlotMap.get(day.dateKey) || [];
    return slots.some((slot) => slot?.start_time ?? slot?.startTime);
  });
  const timeSlotMap = new Map();

  candidateDays.forEach((day) => {
    const slots = daySlotMap.get(day.dateKey) || [];
    slots.forEach((slot) => {
      const startTime = slot?.start_time ?? slot?.startTime;
      if (!startTime) {
        return;
      }
      const slotMoment = moment.tz(startTime, tz);
      if (!slotMoment.isValid()) {
        return;
      }
      const timeLabel = slotMoment.format("h:mm A");
      if (!timeSlotMap.has(timeLabel)) {
        timeSlotMap.set(timeLabel, {
          hour: slotMoment.hour(),
          minute: slotMoment.minute(),
          byDate: new Map(),
        });
      }
      timeSlotMap.get(timeLabel).byDate.set(day.dateKey, slot);
    });
  });

  const availableDateKeys = new Set();
  timeSlotMap.forEach(({ byDate }) => {
    byDate.forEach((_, dateKey) => availableDateKeys.add(dateKey));
  });
  const visibleDays = candidateDays.filter((day) =>
    availableDateKeys.has(day.dateKey)
  );

  const sortedTimes = Array.from(timeSlotMap.entries()).sort((a, b) => {
    const aMinutes = a[1].hour * 60 + a[1].minute;
    const bMinutes = b[1].hour * 60 + b[1].minute;
    return aMinutes - bMinutes;
  });

  const periodRows = {
    morning: [],
    afternoon: [],
    evening: [],
  };

  sortedTimes.forEach(([timeLabel, { hour, byDate }]) => {
    const periodId = getPeriodId(hour);
    const cells = visibleDays.map((day) => {
      const slot = byDate.get(day.dateKey);
      if (!slot) {
        return { status: "booked" };
      }
      return {
        status: "available",
        dayKey: day.dayKey,
        dateKey: day.dateKey,
        time: timeLabel,
        schedulingUrl: extractSchedulingUrl(slot),
        slot,
      };
    });
    periodRows[periodId].push({ time: timeLabel, cells });
  });

  const periods = ["morning", "afternoon", "evening"]
    .map((id) => ({ id, rows: periodRows[id] }))
    .filter((period) => period.rows.length > 0);

  return {
    weekLabel: i18n.t("NUTRITION_TIMESLOT_LBL_WEEK_OF", {
      date: start.format("MMMM D, YYYY"),
    }),
    modalityLabel: extractWeekModalityLabelFromSlots(daySlotMap, visibleDays),
    days: visibleDays,
    periods,
  };
};

export const fetchRegistrationAppointmentWeek = async ({
  rdId,
  rawProfile,
  language,
  apiService,
  signal,
  weekOffset = 0,
}) => {
  const timezone = getRegistrationTimezone();
  const payload = buildListTimeslotByRdPayload(
    rdId,
    rawProfile,
    timezone,
    weekOffset
  );
  const res = await apiService.listRegistrationTimeslotByRd(payload, language, {
    signal,
  });

  if (!res?.status) {
    throw new Error(res?.message || "NUTRITION_TIMESLOT_ERR_LOAD");
  }

  const rdSlot = Array.isArray(res?.data?.rd_slot)
    ? res.data.rd_slot
    : Array.isArray(res?.data)
      ? res.data
      : [];
  const appointmentWeek = mapRdSlotResponseToAppointmentWeek(
    rdSlot,
    payload.start_date,
    payload.end_date,
    payload.timezone
  );

  return {
    payload,
    rdSlot,
    appointmentWeek,
  };
};
